import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Behavior:
 * - Mobile (<750px): DO NOT set source src => no network download; keep poster visible
 * - Desktop (>=750px) and no prefers-reduced-motion: hydrate sources + load video
 * - Optional: only hydrate when component enters viewport (rootMargin preloads early)
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  // Match your Liquid breakpoint: (min-width: 750px)
  static DESKTOP_MQ = '(min-width: 750px)';
  static IO_MARGIN = '200px';

  connectedCallback() {
    super.connectedCallback();

    this._desktopMQ = window.matchMedia(VideoBackgroundComponent.DESKTOP_MQ);
    this._reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._onMQChange = () => this._sync();
    this._desktopMQ.addEventListener?.('change', this._onMQChange);
    this._reduceMQ.addEventListener?.('change', this._onMQChange);

    // Defer heavy work until visible (helps LCP/Speed Index)
    this._io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this._isVisible = true;
          this._sync();
        }
      },
      { rootMargin: VideoBackgroundComponent.IO_MARGIN }
    );

    this._io.observe(this);

    // Initial sync (handles cases where IO doesn't fire immediately)
    this._sync();
  }

  disconnectedCallback() {
    this._desktopMQ?.removeEventListener?.('change', this._onMQChange);
    this._reduceMQ?.removeEventListener?.('change', this._onMQChange);
    this._io?.disconnect();
    super.disconnectedCallback?.();
  }

  _shouldEnableVideo() {
    return this._desktopMQ?.matches && !this._reduceMQ?.matches;
  }

  _hydrateSources() {
    const { videoSources } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource && !source.getAttribute('src')) {
        source.setAttribute('src', videoSource);
      }
    }
  }

  _dehydrateSources() {
    const { videoSources } = this.refs;

    for (const source of videoSources) {
      if (source.hasAttribute('src')) {
        source.removeAttribute('src');
      }
    }
  }

  _sync() {
    const { videoElement } = this.refs;

    const enable = this._shouldEnableVideo();

    // If not desktop or reduced motion: ensure we do NOT load video.
    if (!enable) {
      // If we previously hydrated (e.g. resize / theme editor), undo it.
      this._dehydrateSources();
      try {
        videoElement.pause?.();
      } catch (_) {}
      // Force browser to drop pending requests if any
      try {
        videoElement.load?.();
      } catch (_) {}
      return;
    }

    // Desktop: only load when component is near/inside viewport
    if (!this._isVisible) return;

    this._hydrateSources();
    videoElement.load();
    // Autoplay should work since it's muted/playsinline; ignore failures gracefully
    videoElement.play?.().catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}